// Bulk-import tenant metafield/field DEFINITIONS from a CSV into config.metafields.
//
// Produces the exact field-object shape FieldsSection creates via addCustom:
//   { key, label, dept, kind: 'metafield'|'standard', type, shopifyKey, columnHeader, scored }
//
// Column matching is deliberately loose (via findCol) so BOTH a simple template
// and Shopify's metafield-definition export ("Name", "Namespace", "Key", "Type")
// work without the tenant reshaping their file.

import { parseCSV, findCol } from './csv';

// Canonical Shopify metafield types this app supports — mirrors METAFIELD_TYPES in
// TenantAdminView's FieldsSection. Keep in sync if that list changes.
export const METAFIELD_TYPE_VALUES = [
  'single_line_text_field', 'multi_line_text_field', 'number_integer', 'number_decimal',
  'list.single_line_text_field', 'list.product_reference', 'list.page_reference',
];

// Friendly aliases → canonical type, so a human-written "text" / "long text" / "list"
// and Shopify's own type strings all resolve to something valid.
const TYPE_ALIASES = {
  'text': 'single_line_text_field',
  'single line text': 'single_line_text_field',
  'string': 'single_line_text_field',
  'longtext': 'multi_line_text_field',
  'long text': 'multi_line_text_field',
  'multiline': 'multi_line_text_field',
  'multi line text': 'multi_line_text_field',
  'rich text': 'multi_line_text_field',
  'integer': 'number_integer',
  'int': 'number_integer',
  'number': 'number_integer',
  'decimal': 'number_decimal',
  'float': 'number_decimal',
  'list': 'list.single_line_text_field',
  'list text': 'list.single_line_text_field',
  'products': 'list.product_reference',
  'product': 'list.product_reference',
  'pages': 'list.page_reference',
  'page': 'list.page_reference',
};

// Returns a canonical type string, or null if the value was given but unrecognised.
const canonicalType = (raw) => {
  const v = String(raw || '').trim().toLowerCase();
  if (!v) return 'single_line_text_field';
  if (METAFIELD_TYPE_VALUES.includes(v)) return v;
  return TYPE_ALIASES[v] || null;
};

const isLongType = (t) => t === 'multi_line_text_field' || t === 'list.single_line_text_field';

// Internal product-object key: a lowercase slug, always non-empty.
const slugify = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const truthy = (s) => /^(y|yes|true|1|on)$/i.test(String(s || '').trim());

// Parse CSV text → { fields, errors }. `deptIds` scopes the Department column to
// the tenant's real departments (unknown/blank → 'digital', or the first dept).
export function parseMetafieldCsv(text, { deptIds = ['digital'] } = {}) {
  let rows;
  try { rows = parseCSV(text); } catch (_) { return { fields: [], errors: ['Could not read the CSV file.'] }; }
  if (!rows.length) return { fields: [], errors: ['The CSV has no data rows.'] };

  const defaultDept = deptIds.includes('digital') ? 'digital' : (deptIds[0] || 'digital');
  const deptBySlug = Object.fromEntries(deptIds.map(d => [slugify(d), d]));

  const fields = [];
  const errors = [];
  const usedKeys = new Set();

  rows.forEach((row, i) => {
    const line = i + 2; // +1 header row, +1 for 1-indexing

    const label = findCol(row, 'label', 'name', 'field', 'field name', 'display name', 'title').trim();
    if (!label) { errors.push(`Row ${line}: missing Label — skipped.`); return; }

    const exportAs = findCol(row, 'export as', 'export', 'kind', 'output', 'destination').trim().toLowerCase();
    const isStandard = /column|native|standard/.test(exportAs);

    // namespace.key: a combined column wins; otherwise assemble from Namespace + Key.
    let nsKey = findCol(row, 'namespace.key', 'namespace key', 'full key').trim();
    if (!nsKey) {
      const ns  = findCol(row, 'namespace', 'ns').trim();
      const key = findCol(row, 'key', 'metafield key').trim();
      nsKey = ns && key ? `${ns}.${key}` : (key || ns || '');
    }
    nsKey = nsKey.replace(/\s/g, '');

    const dept = deptBySlug[slugify(findCol(row, 'department', 'dept', 'section'))] || defaultDept;
    const scored = truthy(findCol(row, 'scored', 'score', 'counts toward completion'));

    const typeRaw = findCol(row, 'type', 'field type', 'metafield type', 'content type').trim();
    let type;
    if (isStandard) {
      type = isLongType(canonicalType(typeRaw)) ? 'longtext' : 'text';
    } else {
      const canon = canonicalType(typeRaw);
      if (typeRaw && !canon) errors.push(`Row ${line}: unknown type "${typeRaw}" — defaulted to Text.`);
      type = canon || 'single_line_text_field';
    }

    if (!isStandard && !nsKey) { errors.push(`Row ${line}: "${label}" has no namespace/key — skipped.`); return; }
    const columnHeader = isStandard ? (findCol(row, 'column header', 'header', 'csv column').trim() || label) : '';

    // Unique internal key within this batch.
    const base = slugify(nsKey || label) || 'field';
    let key = base, n = 2;
    while (usedKeys.has(key)) key = `${base}_${n++}`;
    usedKeys.add(key);

    fields.push({
      key, label, dept,
      kind: isStandard ? 'standard' : 'metafield',
      type,
      shopifyKey: isStandard ? '' : nsKey,
      columnHeader,
      scored,
    });
  });

  return { fields, errors };
}

// A ready-to-fill template so the expected columns are unambiguous.
export function metafieldTemplateCsv() {
  return [
    'Label,Namespace,Key,Type,Export as,Department,Scored',
    'Materials,specifications,materials,text,metafield,technical,no',
    'Care Notes,care,notes,long text,metafield,technical,no',
    'Season Tags,marketing,season,list,metafield,digital,no',
  ].join('\n');
}
